import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Lock, User, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, applySession } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [googleProcessing, setGoogleProcessing] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  const from = location.state?.from?.pathname || '/book';

  // Handle return from Emergent Google OAuth (session_id in URL fragment)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sid = new URLSearchParams(hash.slice(1)).get('session_id');
      window.history.replaceState(null, '', window.location.pathname);
      if (sid) {
        setGoogleProcessing(true);
        axios
          .post(`${API}/auth/google/session`, { session_id: sid })
          .then((res) => {
            const { access_token, user } = res.data;
            applySession(access_token, user);
            toast.success(`Welcome, ${user.name}!`);
            navigate(user.role === 'admin' ? '/admin' : from, { replace: true });
          })
          .catch(() => {
            toast.error('Google sign-in failed. Please try again.');
            setGoogleProcessing(false);
          });
      }
    }
  }, []); // eslint-disable-line

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate(userData?.role === 'admin' ? '/admin' : from, { replace: true });
        return;
      } else {
        if (!formData.name) {
          toast.error('Please enter your name');
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
        toast.success('Account created successfully!');
      }
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
      {googleProcessing && (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="google-processing-overlay">
          <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
          <p className="mt-4 text-white font-medium">Signing you in with Google…</p>
        </div>
      )}
      <header className="border-b border-zinc-800 backdrop-blur-sm bg-black/30">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <Button 
            variant="ghost" 
            className="text-zinc-300 hover:text-white hover:bg-white/10"
            onClick={() => navigate('/')}
            data-testid="back-home-auth-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="max-w-md mx-auto px-6 py-20">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/advll02n_ChatGPT%20Image%20Jul%2011%2C%202026%2C%2004_01_30%20PM.png" 
            alt="CarLens" 
            className="h-24 w-auto max-w-[280px] sm:h-28 md:h-40 md:max-w-none mx-auto mb-6 object-contain"
          />
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-zinc-400">
            {isLogin ? 'Sign in to access your inspection portal' : 'Join CarLens to track your inspections'}
          </p>
        </div>

        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8">
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading || googleProcessing}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-zinc-100 text-zinc-800 font-semibold py-3.5 rounded-lg transition-colors disabled:opacity-60"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001 6.19 5.238 6.19 5.238-.438.398 6.594-4.807 6.594-14.809 0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            {googleProcessing ? 'Signing you in…' : 'Continue with Google'}
          </button>

          <div className="flex items-center gap-3 my-6">
            <span className="h-px flex-1 bg-zinc-800" />
            <span className="text-xs uppercase tracking-wide text-zinc-500">or</span>
            <span className="h-px flex-1 bg-zinc-800" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-zinc-300 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Full Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required={!isLogin}
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  placeholder="John Doe"
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-zinc-300 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                placeholder="you@example.com"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-300 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Password
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                placeholder="••••••••"
                minLength={isLogin ? undefined : 6}
                data-testid="password-input"
              />
              {!isLogin && (
                <p className="text-xs text-zinc-500 mt-1">Minimum 6 characters</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 text-lg font-semibold rounded-lg"
              data-testid="submit-auth-btn"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-orange-400 hover:text-orange-300 text-sm"
              data-testid="toggle-auth-mode-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;